// Utility functions for the application

// Format currency in Thai Baht
export const formatCurrency = (amount, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    showSymbol = false
  } = options
  
  const formatter = new Intl.NumberFormat('th-TH', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'THB',
    minimumFractionDigits,
    maximumFractionDigits
  })
  
  return formatter.format(amount || 0)
}

// Format number with Thai locale
export const formatNumber = (number, options = {}) => {
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2
  } = options
  
  const formatter = new Intl.NumberFormat('th-TH', {
    minimumFractionDigits,
    maximumFractionDigits
  })
  
  return formatter.format(number || 0)
}

export function numberToThaiText(number) {
  number = Number(number);
  if (isNaN(number)) return 'ศูนย์บาทถ้วน';

  const txtNumArr = ['ศูนย์','หนึ่ง','สอง','สาม','สี่','ห้า','หก','เจ็ด','แปด','เก้า'];
  const txtDigitArr = ['','สิบ','ร้อย','พัน','หมื่น','แสน','ล้าน'];

  let bahtText = '';
  let [baht, satang] = number.toFixed(2).split('.');
  baht = parseInt(baht, 10).toString();

  // ฟังก์ชันย่อยแปลงตัวเลขหลักให้เป็นคำอ่าน
  const convert = (num) => {
      let text = '';
      for (let i = 0; i < num.length; i++) {
          const digit = parseInt(num.charAt(num.length - i - 1));
          const position = i % 6;
          const isLastRound = i >= 6 && position === 0;
          const digitText = txtNumArr[digit];
          const digitPlace = txtDigitArr[position];

          if (digit !== 0) {
              if (position === 0 && i > 0 && digit === 1 && !isLastRound) {
                  text = 'เอ็ด' + text;
              } else if (position === 1 && digit === 2) {
                  text = 'ยี่' + digitPlace + text;
              } else if (position === 1 && digit === 1) {
                  text = digitPlace + text;
              } else {
                  text = digitText + digitPlace + text;
              }
          } else if (isLastRound) {
              text = txtDigitArr[6] + text;
          }
          if (i === 5 && num.length - i - 1 > 0) {
              text = txtDigitArr[6] + text;
          }
      }
      return text;
  };

  // แปลงส่วนบาท
  if (parseInt(baht, 10) === 0) {
      bahtText = 'ศูนย์บาท';
  } else {
      bahtText = convert(baht) + 'บาท';
  }

  // แปลงส่วนสตางค์
  if (parseInt(satang, 10) === 0) {
      bahtText += 'ถ้วน';
  } else {
      bahtText += convert(satang) + 'สตางค์';
  }

  return bahtText;
}


// Format date in Thai format
export const formatDate = (date, format = 'DD/MM/YYYY') => {
  if (!date) return '-'
  
  const dayjs = require('dayjs')
  require('dayjs/locale/th')
  
  return dayjs(date).locale('th').format(format)
}

// Format datetime in Thai format
export const formatDateTime = (date, format = 'DD/MM/YYYY HH:mm') => {
  if (!date) return '-'
  
  const dayjs = require('dayjs')
  require('dayjs/locale/th')
  
  return dayjs(date).locale('th').format(format)
}

// Get status color for badges
export const getStatusColor = (status) => {
  const statusColors = {
    // General statuses
    active: '#52c41a',
    inactive: '#d9d9d9',
    pending: '#faad14',
    approved: '#52c41a',
    rejected: '#ff4d4f',
    cancelled: '#ff4d4f',
    
    // Receipt/Invoice statuses
    draft: '#d9d9d9',
    sent: '#1890ff',
    paid: '#52c41a',
    partial: '#faad14',
    overdue: '#ff4d4f',
    invoice: '#1890ff',
    
    // Quote statuses
    accepted: '#52c41a',
    expired: '#ff4d4f',
    
    // Priority levels
    high: '#ff4d4f',
    medium: '#faad14',
    low: '#52c41a'
  }
  
  return statusColors[status] || '#d9d9d9'
}

// Get status text in Thai
export const getStatusText = (status) => {
  const statusTexts = {
    // General statuses
    active: 'ใช้งาน',
    inactive: 'ไม่ใช้งาน',
    pending: 'รออนุมัติ',
    approved: 'อนุมัติแล้ว',
    rejected: 'ปฏิเสธ',
    cancelled: 'ยกเลิก',
    
    // Receipt/Invoice statuses
    draft: 'แบบร่าง',
    sent: 'ส่งแล้ว',
    paid: 'ชำระแล้ว',
    partial: 'ชำระบางส่วน',
    overdue: 'ครบกำหนด',
    invoice: 'ใบแจ้งหนี้',
    
    // Quote statuses
    accepted: 'ยอมรับ',
    expired: 'หมดอายุ',
    
    // Priority levels
    high: 'สูง',
    medium: 'ปานกลาง',
    low: 'ต่ำ'
  }
  
  return statusTexts[status] || status
}

// Validate email format
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Validate Thai phone number
export const isValidPhoneNumber = (phone) => {
  const phoneRegex = /^[0-9]{9,10}$/
  const cleanPhone = phone.replace(/[^0-9]/g, '')
  return phoneRegex.test(cleanPhone)
}

// Validate Thai tax ID
export const isValidTaxId = (taxId) => {
  const taxIdRegex = /^[0-9]{13}$/
  const cleanTaxId = taxId.replace(/[^0-9]/g, '')
  return taxIdRegex.test(cleanTaxId)
}

// Generate random ID
export const generateId = () => {
  return Math.random().toString(36).substr(2, 9)
}

// Debounce function
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Deep clone object
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }
  
  if (obj instanceof Date) {
    return new Date(obj.getTime())
  }
  
  if (obj instanceof Array) {
    return obj.map(item => deepClone(item))
  }
  
  if (typeof obj === 'object') {
    const clonedObj = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }
}

// Calculate percentage
export const calculatePercentage = (value, total) => {
  if (!total || total === 0) return 0
  return Math.round((value / total) * 100)
}

// Calculate percentage change
export const calculatePercentageChange = (current, previous) => {
  if (!previous || previous === 0) return 0
  return Math.round(((current - previous) / previous) * 100)
}

// Get file size in human readable format
export const getFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Get file extension
export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
}

// Check if file is image
export const isImageFile = (filename) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  const extension = getFileExtension(filename).toLowerCase()
  return imageExtensions.includes(extension)
}

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

// Download file
export const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Copy text to clipboard
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    return true
  }
}

// Local storage helpers
export const storage = {
  get: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key)
      return item ? JSON.parse(item) : defaultValue
    } catch (error) {
      console.error('Error reading from localStorage:', error)
      return defaultValue
    }
  },
  
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
      return true
    } catch (error) {
      console.error('Error writing to localStorage:', error)
      return false
    }
  },
  
  remove: (key) => {
    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error('Error removing from localStorage:', error)
      return false
    }
  },
  
  clear: () => {
    try {
      localStorage.clear()
      return true
    } catch (error) {
      console.error('Error clearing localStorage:', error)
      return false
    }
  }
}